import { useCallback, useState } from 'react';
import { Box, Paper, Typography } from '@mui/material';

// Shared panel primitives extracted from the device Live Dashboard so the
// Fleet Analytics page can reuse the same chrome + visibility persistence.
// (One source of truth — device-live-dashboard imports these.)

/** MUI Grid v2 responsive size shape used by panel registries. */
export interface PanelGridSize {
  xs: number;
  sm: number;
  md: number;
  lg?: number;
}

/**
 * Show/hide persistence for a panel registry. Generic over the panel-id
 * union and parameterized by localStorage key so multiple dashboards keep
 * independent layouts (e.g. 'device-dashboard-panels',
 * 'fleet-analytics-panels').
 */
// eslint-disable-next-line react-refresh/only-export-components -- hook co-located with its panel component (Fast Refresh dev-only)
export function usePanelVisibility<Id extends string>(
  storageKey: string,
  defaults: Record<Id, boolean>
) {
  const [vis, setVis] = useState<Record<Id, boolean>>(() => {
    if (typeof window === 'undefined') return defaults;
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored) as Partial<Record<Id, boolean>>;
        return { ...defaults, ...parsed };
      }
    } catch { /* sandbox */ }
    return defaults;
  });
  const toggle = useCallback((id: Id) => {
    setVis((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch { /* sandbox */ }
      return next;
    });
  }, [storageKey]);
  return [vis, toggle] as const;
}

/**
 * Panel chrome: outlined paper with an uppercase title bar. `action` renders
 * right-aligned in the title bar (per-panel period selectors etc.).
 */
export function PanelFrame({
  title,
  icon,
  children,
  minHeight = 180,
  dense = false,
  action,
  draggable = false,
  fill = false,
}: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  minHeight?: number;
  dense?: boolean;
  action?: React.ReactNode;
  /** Title bar becomes the drag handle (react-grid-layout draggableHandle). */
  draggable?: boolean;
  /** Fill the parent's height — for grid items that have a definite height. */
  fill?: boolean;
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: 1,
        ...(fill ? { height: '100%' } : { minHeight }),
      }}
    >
      <Box
        className={draggable ? 'panel-drag-handle' : undefined}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.5,
          py: 0.75,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: '#FAFAFA',
          ...(draggable ? { cursor: 'move', userSelect: 'none' } : {}),
        }}
      >
        {icon}
        <Typography variant="caption" fontWeight={700} sx={{ color: '#444', letterSpacing: 0.3, flex: 1 }}>
          {title.toUpperCase()}
        </Typography>
        {action}
      </Box>
      <Box sx={{ flex: 1, p: dense ? 0 : 1.5, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {children}
      </Box>
    </Paper>
  );
}
